<script setup lang="ts">
import type { VTableFilterPreviewCount } from "./VTable.types";
import { onClickOutside } from "@vueuse/core";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useTableFilters } from "../../composables/Table/useTableFilters";
import VDropdown from "../Dropdown/VDropdown.vue";
import VTableFilterPanel from "./VTableFilterPanel.vue";
import VTableToolbarButton from "./VTableToolbarButton.vue";

defineOptions({
  name: "VTableFilterToolbar",
});

/** The Filters button of a VTable toolbar, with the number of active filters, opening the filter panel. */
const {
  previewCount = undefined,
  itemLabel = undefined,
  itemsLabel = undefined,
  large = false,
} = defineProps<{
  previewCount?: VTableFilterPreviewCount
  itemLabel?: string
  itemsLabel?: string
  /** 44px touch target, for phones. */
  large?: boolean
}>();

const { t } = useI18n();
const { activeFilters } = useTableFilters();
const wrapperRef = ref<HTMLElement | null>(null);
const open = ref(false);
/** A new key per opening, so the panel starts from the applied filters again. */
const openCount = ref(0);

const activeCount = computed(() => activeFilters.value.length);
const buttonLabel = computed(() => (activeCount.value > 0
  ? t("filters.button_label_active", { count: activeCount.value }, activeCount.value)
  : t("filters.filters")));

function toggle() {
  if (open.value) {
    close();
    return;
  }
  openCount.value++;
  open.value = true;
}

function close() {
  open.value = false;
}

/** Closes the popover and puts focus back on its button, so Escape works from the button and from the panel. */
function closeAndRefocus() {
  if (!open.value) {
    return;
  }
  open.value = false;
  void nextTick(() => wrapperRef.value?.querySelector<HTMLElement>("button")?.focus());
}

onClickOutside(wrapperRef, () => {
  if (open.value) {
    close();
  }
}, { ignore: [".v-popper__popper"] });
</script>

<template>
  <div ref="wrapperRef" class="inline-flex shrink-0" @keydown.esc="closeAndRefocus">
    <VDropdown
      theme="verbleif-popper"
      placement="bottom-start"
      :distance="6"
      :shown="open"
      :triggers="[]"
      :auto-hide="false"
      @hide="open = false"
    >
      <VTableToolbarButton
        icon="funnel"
        :label="buttonLabel"
        :active="activeCount > 0"
        :open="open"
        :badge="activeCount"
        haspopup="dialog"
        :large="large"
        data-testid="table-filter-button"
        @click="toggle"
      >
        {{ t("filters.filters") }}
      </VTableToolbarButton>

      <template #popper>
        <VTableFilterPanel
          v-if="open"
          :key="openCount"
          :preview-count="previewCount"
          :item-label="itemLabel ?? t('base.table.item')"
          :items-label="itemsLabel ?? t('base.table.items')"
          @close="closeAndRefocus"
        />
      </template>
    </VDropdown>
  </div>
</template>
